import { useEffect, useRef, useState } from 'react';
import { cx, FIELD_CLASS, Spinner } from '@/components/ui';
import { useDebounced, useStockSearch } from './useStocks';
import { MIN_SEARCH_LENGTH, type StockSummary } from './stockTypes';

/**
 * 종목 검색창. (16.1)
 *
 * 검색을 별도 페이지로 두지 않았다. 검색은 목적지가 아니라 행동이다.
 * /trading 상단에 상시 노출하고, 결과를 클릭하면 모달이 열린다.
 * (docs/01-trading.md 8.1절 화면)
 *
 * 🔴 검색은 우리 DB 를 본다. 토스를 부르지 않는다
 *   토스에는 이름으로 찾는 엔드포인트가 없다. symbols 파라미터 하나뿐이다.
 *   우리는 td_stock 에 4,296종목을 갖고 있고 로컬 검색이 더 빠르다.
 */

interface Props {
  onSelect: (stock: StockSummary) => void;
  /** 검색 범위. 국내/해외 탭이 정한다 (2026-08-20). 생략하면 국내다 */
  country?: 'KR' | 'US';
}

export function StockSearchBox({ onSelect, country = 'KR' }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // 한 글자마다 요청이 나가지 않게 늦춘다.
  // 한글은 조합 중에도 입력 이벤트가 발생해 실제 횟수가 글자 수보다 많다.
  const debouncedQuery = useDebounced(query);
  const { data: results, isFetching } = useStockSearch(debouncedQuery, country);

  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH;
  const items = results ?? [];

  // 바깥을 누르면 닫는다. 드롭다운이 열린 채로 남아 화면을 가리는 것을 막는다.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  // 검색어가 바뀌면 선택 위치를 처음으로 돌린다.
  // 없으면 3번째 항목을 고른 상태에서 검색어를 바꿨을 때
  // 엉뚱한 종목이 하이라이트된 채로 남는다.
  useEffect(() => {
    setHighlighted(0);
  }, [debouncedQuery]);

  function choose(stock: StockSummary) {
    onSelect(stock);
    setOpen(false);
    // 검색어는 남긴다. 모달을 닫고 다른 종목을 이어 보는 경우가 많다.
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    // 🔴 한글 조합 중에는 키를 가로채지 않는다
    //   조합 중 Enter 는 "글자를 확정" 하는 뜻이라 여기서 종목을 골라버리면
    //   "삼성" 을 치다가 첫 결과가 열린다.
    if (event.nativeEvent.isComposing) {
      return;
    }

    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (items.length === 0) {
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlighted((index) => Math.min(index + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlighted((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const stock = items[highlighted];
      if (stock) {
        choose(stock);
      }
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={
          country === 'US'
            ? '종목명 또는 티커 (예: SanDisk, SNDK)'
            : '종목명 또는 종목코드 (예: 삼성전자, 005930)'
        }
        className={FIELD_CLASS}
        aria-label="종목 검색"
        autoComplete="off"
      />

      {open && trimmed.length > 0 && (
        <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-lg border border-line bg-surface shadow-lg">
          {tooShort && (
            <p className="px-3 py-3 text-sm text-fg-muted">
              두 글자 이상 입력하세요
            </p>
          )}

          {!tooShort && isFetching && items.length === 0 && (
            <div className="px-3 py-3">
              <Spinner label="찾는 중" />
            </div>
          )}

          {!tooShort && !isFetching && items.length === 0 && (
            <p className="px-3 py-3 text-sm text-fg-muted">
              «{trimmed}» 에 해당하는 종목이 없습니다
            </p>
          )}

          {items.length > 0 && (
            <ul role="listbox" className="max-h-72 overflow-y-auto">
              {items.map((stock, index) => (
                <li key={stock.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === highlighted}
                    onMouseEnter={() => setHighlighted(index)}
                    onClick={() => choose(stock)}
                    className={cx(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm',
                      index === highlighted ? 'bg-surface-hover' : 'bg-surface',
                    )}
                  >
                    <span className="font-medium text-fg">{stock.name}</span>
                    <span className="font-mono text-xs text-fg-muted">
                      {stock.symbol}
                    </span>
                    <span className="ml-auto text-xs text-fg-subtle">
                      {stock.market}
                      {stock.securityType !== 'STOCK' && ` · ${stock.securityType}`}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
